<template>
    <hts-drawer
        v-model="visible"
        :rules="rulesEditTask"
        :submit="handleSubmit"
        size="large"
        :title="$t('labels.taskModule.edit')"
        :undo-enable="true"
        @close="closeOverSide">
        <div class="hts-vehicles-ignore-add-vehicle">
            <div class="hts-vehicles-ignore-add-vehicle__container-body">
                <hts-card :title="$t('labels.generalInformation')">
                    <hts-form-radio
                        v-model="dataEdit.taskType"
                        class="col-span-2"
                        :label="$t('labels.type')"
                        :items="taskTypes"
                        type="list"
                        required
                        value-key="key"
                        label-key="label" />

                    <task-name
                        v-model="dataEdit.type"
                        v-model:text="dataEdit.name"
                        :label="$t('labels.taskModule.name')"
                        :required="true"
                        :color="props.color"
                        name="taskModule.name"
                        prop="name"
                        :item-name="itemName" />

                    <hts-form-input
                        v-model="dataEdit.description"
                        :label="$t('labels.description')"
                        :color="props.color"
                        name="description"
                        prop="description" />

                    <div v-if="dataEdit.taskType === 'AFFECT_SHIPPING_TIME'">
                        <div class="flex">
                            <hts-form-checkbox
                                v-model="dataEdit.canSplitWay"
                                :checked="!!dataEdit.canSplitWay"
                                :items="[{ label: $t('labels.canSplitWay'), value: true }]" />
                            <hts-tooltip class="mt-1">
                                <div>
                                    <span class="font-bold">
                                        {{ $t('labels.canSplitWayInformation') }}
                                    </span>
                                </div>
                                <template #reference>
                                    <hi-information-circle-icon class="flex items-center text-green-500 w-6 h-6" />
                                </template>
                            </hts-tooltip>
                        </div>

                        <div class="flex">
                            <hts-form-checkbox
                                v-model="dataEdit.requireContainerNumber"
                                :checked="!!dataEdit.requireContainerNumber"
                                :items="[{ label: $t('labels.requireContainerNumber'), value: true }]" />
                        </div>

                        <div class="flex">
                            <hts-form-checkbox
                                v-model="dataEdit.documentRequired"
                                :checked="!!dataEdit.documentRequired"
                                :items="[{ label: $t('labels.documentRequired'), value: true }]" />
                        </div>

                        <div class="flex">
                            <hts-form-checkbox
                                v-model="dataEdit.taskStartEndDurationRequired"
                                :checked="!!dataEdit.taskStartEndDurationRequired"
                                :items="[{ label: $t('labels.taskStartEndDurationRequired'), value: true }]" />
                        </div>

                        <div class="flex">
                            <hts-form-checkbox
                                v-model="dataEdit.allowCustomerViewDocument"
                                :checked="!!dataEdit.allowCustomerViewDocument"
                                :items="[{ label: $t('labels.allowCustomerViewDocument'), value: true }]" />
                        </div>
                    </div>
                </hts-card>
                <hts-card
                    v-if="dataEdit.taskType === 'AFFECT_SHIPPING_TIME'"
                    :title="$t('labels.earliestLatest')">
                    <div
                        v-for="(timeCondition, index) in dataEdit.timeConditions"
                        :key="timeCondition">
                        <hr
                            v-if="index > 0"
                            class="my-4" >
                        <hts-form-input
                            v-model="timeCondition.name"
                            :label="$t('labels.earliestLatestName')"
                            :color="props.color"
                            :required="!!timeCondition.type" />
                        <hts-form-radio
                            v-model="timeCondition.type"
                            :items="timeConditionTypes"
                            value-key="key"
                            label-key="label"
                            :required="!!timeCondition.name"
                            class="w-full" />

                        <div class="grid grid-cols-2">
                            <div>
                                <hts-form-radio
                                    v-if="timeCondition.type === 'START'"
                                    v-model="timeCondition.timeType"
                                    class="ml-5"
                                    :items="timeConditionTimeTypes"
                                    value-key="key"
                                    label-key="label"
                                    required />
                            </div>
                            <div>
                                <hts-form-radio
                                    v-if="timeCondition.type === 'END'"
                                    v-model="timeCondition.timeType"
                                    class="ml-5"
                                    :items="timeConditionTimeTypes"
                                    value-key="key"
                                    label-key="label"
                                    required />
                            </div>
                        </div>

                        <div class="text-right">
                            <hts-form-button
                                size="small"
                                color="error"
                                @click="deleteTimeCondition(index)">
                                {{ $t('labels.delete') }}
                            </hts-form-button>
                        </div>
                    </div>

                    <hts-form-button
                        size="small"
                        @click="addTimeCondition">
                        {{ $t('labels.add') }}
                    </hts-form-button>
                </hts-card>

                <hts-card :title="$t('labels.completionTime')">
                    <div class="w-full overflow-x-auto">
                        <div class="min-w-[1024px]">
                            <hts-form-checkbox
                                v-model="dataEdit.completionTimeCategories"
                                :label="$t('labels.transportation')"
                                required
                                :items="transportationOptions()" />
                            <div class="flex flex-col gap-2">
                                <div class="grid-cols-4 grid gap-x-2">
                                    <p class="col-span-2 mb-2">
                                        {{ $t('labels.type') }}
                                    </p>
                                    <p :class="{ 'col-span-2': dataEdit.taskType === 'NO_AFFECT_SHIPPING_TIME' }">
                                        {{ $t('labels.averageCompletionTime') }} <span class="text-error-500">
                                            *
                                        </span>
                                    </p>
                                    <p v-if="dataEdit.taskType === 'AFFECT_SHIPPING_TIME'">
                                        {{ $t('labels.minimumCompletionTime') }} <span class="text-error-500">
                                            *
                                        </span>
                                    </p>
                                </div>
                                <template v-if="!dataEdit.completionTimeCategories || dataEdit.completionTimeCategories.includes(0)">
                                    <div
                                        v-for="(completionTimeVehicle, index) in dataEdit.completionTimes?.vehicleCategory"
                                        :key="completionTimeVehicle"
                                        class="grid-cols-4 grid gap-x-2">
                                        <hts-form-input
                                            v-model="completionTimeVehicle.name"
                                            class="col-span-2"
                                            :color="color"
                                            disabled />
                                        <hts-form-input
                                            v-model="completionTimeVehicle.averageTime"
                                            :class="{ 'col-span-2': dataEdit.taskType === 'NO_AFFECT_SHIPPING_TIME' }"
                                            type="number"
                                            :color="color"
                                            required
                                            :number-decimal="1"
                                            :prop="`vehicleCategory-average-${completionTimeVehicle.id}`"
                                            name="averageCompletionTime"
                                            @change="(val) => completionTimeChange('vehicleCategory', index, val)" />
                                        <hts-form-input
                                            v-if="dataEdit.taskType === 'AFFECT_SHIPPING_TIME'"
                                            v-model="completionTimeVehicle.minimumTime"
                                            type="number"
                                            :color="color"
                                            required
                                            :number-decimal="1"
                                            :prop="`vehicleCategory-minimum-${completionTimeVehicle.id}`"
                                            name="minimumCompletionTime"
                                            @change="minimumTimeChange('vehicleCategory', index)" />
                                    </div>
                                </template>
                                <template v-if="!dataEdit.completionTimeCategories || dataEdit.completionTimeCategories.includes(1)">
                                    <div
                                        v-for="(completionTimeCont, index) in dataEdit.completionTimes?.contCategory"
                                        :key="completionTimeCont"
                                        class="grid-cols-4 grid gap-x-2">
                                        <hts-form-input
                                            v-model="completionTimeCont.name"
                                            class="col-span-2"
                                            :color="color"
                                            disabled />
                                        <hts-form-input
                                            v-model="completionTimeCont.averageTime"
                                            :class="{ 'col-span-2': dataEdit.taskType === 'NO_AFFECT_SHIPPING_TIME' }"
                                            type="number"
                                            :color="color"
                                            required
                                            :number-decimal="1"
                                            :prop="`contCategory-average-${completionTimeCont.id}`"
                                            name="averageCompletionTime"
                                            @change="(val) => completionTimeChange('contCategory', index, val)" />
                                        <hts-form-input
                                            v-if="dataEdit.taskType === 'AFFECT_SHIPPING_TIME'"
                                            v-model="completionTimeCont.minimumTime"
                                            type="number"
                                            required
                                            :color="color"
                                            :number-decimal="1"
                                            :prop="`contCategory-minimum-${completionTimeCont.id}`"
                                            name="minimumCompletionTime"
                                            @change="minimumTimeChange('contCategory', index)" />
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </hts-card>
            </div>
        </div>
    </hts-drawer>
</template>
<script setup lang="ts">
import cloneDeep from 'lodash-es/cloneDeep'
import { initAddTask, initEditTask } from '../imports/form'
import data from '../imports/data'
import TaskName from './task-name.vue'
import { $api } from '#imports'
const { transportationOptions, transportationTypeKeys, contTypes, taskTypes, timeConditionTypes, timeConditionTimeTypes } = data()
const itemName = ref('')

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeItemInput: {
    type: String,
    default: 'small'
  },
  column: {
    type: Number,
    default: 1
  }
})

const ID = ref('')
const visible = ref(false)

const open = async (id: string) => {
  if (id) {
    ID.value = id
  }
  await getData(id)
  visible.value = true
}

const emits = defineEmits(['close', 'refreshTables', 'update:show'])

const closeOverSide = () => {
  visible.value = false
}

const dataEdit: { [key: string]: any | null } = ref({ ...initEditTask })

const resetField = () => {
  dataEdit.value = { ...initAddTask }
  dataEdit.value.timeConditions = [
    {
      name: '',
      type: '',
      timeType: ''
    }
  ]
  dataEdit.value.completionTimes = {
    vehicleCategory: [
      {
        id: '',
        name: '',
        averageTime: null,
        minimumTime: null
      }
    ],
    contCategory: [
      {
        id: '',
        name: '',
        averageTime: null,
        minimumTime: null
      }
    ]
  }
  itemName.value = ''
}

const getData = async (id: string) => {
  const api = $urlAdmin.business.task.getById
  const data: any = await $api(api, { uriParams: { ID: id } })
  const resData = data?.data?.value

  if (resData?.success) {
    dataEdit.value.name = resData.data.name
    itemName.value = resData.data.name
    dataEdit.value.type = resData.data.type
    dataEdit.value.description = resData.data.description
    dataEdit.value.taskType = resData.data.taskType

    dataEdit.value.canSplitWay = !!resData.data.canSplitWay
    dataEdit.value.requireContainerNumber = !!resData.data.requireContainerNumber

    dataEdit.value.documentRequired = !!resData.data.documentRequired
    dataEdit.value.taskStartEndDurationRequired = !!resData.data.taskStartEndDurationRequired
    dataEdit.value.allowCustomerViewDocument = !!resData.data.allowCustomerViewDocument

    dataEdit.value.timeConditions = resData.data.timeConditions
      ? resData.data.timeConditions
      : [
        {
          name: '',
          type: '',
          timeType: ''
        }
      ]

    dataEdit.value.completionTimes = resData.data.completionTimes
    dataEdit.value.completionTimeCategories = resData.data.completionTimeCategories

    if (!resData.data.completionTimeCategories) {
      dataEdit.value.completionTimeCategories = [0, 1]
    } else {
      dataEdit.value.completionTimeCategories = resData.data.completionTimeCategories
    }
    await setDataEstimatedCompletionTime(dataEdit.value.completionTimes)
  }
}

// reset thời gian tác vụ
const oldCompletionTimeCategories = ref([] as number[])
watch(
  () => dataEdit.value.completionTimeCategories,
  (val: number[]) => {
    if (!visible.value) {
      return
    }

    if (oldCompletionTimeCategories.value?.includes(0) && !val.includes(0)) {
      if (dataEdit.value?.completionTimes?.vehicleCategory) {
        dataEdit.value.completionTimes.vehicleCategory = dataEdit.value.completionTimes?.vehicleCategory?.map((item: any) => {
          item.averageTime = null
          item.minimumTime = null
          item.minimumTimeChanged = false
          return item
        })
      }
    }
    if (oldCompletionTimeCategories.value?.includes(1) && !val.includes(1)) {
      if (dataEdit.value?.completionTimes?.contCategory) {
        dataEdit.value.completionTimes.contCategory = dataEdit.value.completionTimes?.contCategory?.map((item: any) => {
          item.averageTime = null
          item.minimumTime = null
          item.minimumTimeChanged = false
          return item
        })
      }
    }
    oldCompletionTimeCategories.value = cloneDeep(val)
  },
  { deep: true }
)

const setDataEstimatedCompletionTime = async (completionTimes: any) => {
  const completionTimeCont: {
    id: string | number
    name: string
    averageTime: number | null
    minimumTime: number | null
    minimumTimeChanged: boolean
  }[] = []

  const completionTimeVehicle: {
    id: string
    name: string
    averageTime: number | null
    minimumTime: number | null
    minimumTimeChanged: boolean
  }[] = []

  const completionTimesValue = {
    contCategory: completionTimeCont,
    vehicleCategory: completionTimeVehicle
  }

  contTypes.forEach((item: { [key: string]: string | number }) => {
    if (completionTimes?.contCategory?.[item.value]) {
      if (dataEdit.value.completionTimeCategories.includes(1)) {
        completionTimeCont.push({ ...completionTimes?.contCategory[item.value], minimumTimeChanged: true })
      } else {
        completionTimeCont.push({ ...completionTimes?.contCategory[item.value], minimumTimeChanged: false })
      }
    } else {
      completionTimeCont.push({
        id: item.value as number,
        name: item.label as string,
        averageTime: null,
        minimumTime: null,
        minimumTimeChanged: false
      })
    }
  })

  completionTimesValue.contCategory = completionTimeCont
  const api = $urlAdmin.vehicle.category.getAll
  const res: any = await $api(api)
  const resData = res?.data?.value
  if (resData?.success) {
    const vehicleCategories = resData.data
    if (vehicleCategories && vehicleCategories.length) {
      vehicleCategories.forEach((item: { [key: string]: string | number }) => {
        if (completionTimes?.vehicleCategory?.[item._id]) {
          completionTimes.vehicleCategory[item._id].name = item.name
          if (dataEdit.value.completionTimeCategories.includes(0)) {
            completionTimeVehicle.push({ ...completionTimes.vehicleCategory[item._id], minimumTimeChanged: true })
          } else {
            completionTimeVehicle.push({ ...completionTimes.vehicleCategory[item._id], minimumTimeChanged: false })
          }
        } else {
          completionTimeVehicle.push({
            id: item._id as string,
            name: item.name as string,
            averageTime: null,
            minimumTime: null,
            minimumTimeChanged: false
          })
        }
      })
    }
  }

  completionTimesValue.vehicleCategory = completionTimeVehicle

  dataEdit.value.completionTimes = completionTimesValue
}

const handleSubmit = () => {
  const api = $urlAdmin.business.task.edit
  $api(api, {
    uriParams: { ID: ID.value },
    body: dataEdit.value
  }).then(async (data) => {
    const resData: any = data?.data?.value

    if (resData?.success) {
      process.client && $toast().success($t('labels.editTaskSuccess'))
      await resetField()
      closeOverSide()
      emits('refreshTables')
    }
  })
}

const addTimeCondition = () => {
  dataEdit.value.timeConditions.push({
    name: '',
    type: '',
    timeType: ''
  })
}

const deleteTimeCondition = (index: number) => {
  if (!dataEdit?.value?.timeConditions || !Array.isArray(dataEdit?.value?.timeConditions)) {
    return
  }

  if (dataEdit.value.timeConditions.length <= 1) {
    dataEdit.value.timeConditions = [
      {
        name: '',
        type: '',
        timeType: ''
      }
    ]
    return
  }
  dataEdit.value.timeConditions.splice(index, 1)
}

const columnClass = computed(() => {
  if ($lodash.inRange(props.column, 1, 12)) {
    return `grid-cols-${props.column}`
  }

  return 'grid-cols-1'
})

const completionTimeChange = (type: string, index: number, value: number) => {
  try {
    if (!['vehicleCategory', 'contCategory'].includes(type)) {
      return
    }
    if (dataEdit.value.completionTimes?.[type]?.[index]?.minimumTimeChanged) {
      return
    }

    dataEdit.value.completionTimes[type][index].minimumTime = value
  } catch (e) {}
}

const minimumTimeChange = (type: string, index: number) => {
  try {
    if (!dataEdit.value.completionTimes?.[type]?.[index]?.minimumTime) {
      dataEdit.value.completionTimes[type][index].minimumTimeChanged = false
      return
    }

    dataEdit.value.completionTimes[type][index].minimumTimeChanged = true
  } catch (e) {}
}

const rulesEditTask = computed(() => {
  const rules = {
    name: 'required'
  } as { [key: string]: string }

  if (transportationTypeKeys.includes(0) && Array.isArray(dataEdit.value?.completionTimes?.vehicleCategory)) {
    dataEdit.value.completionTimes.vehicleCategory.forEach((type: any) => {
      rules[`vehicleCategory-average-${type.id}`] = `gt:0`
      rules[`vehicleCategory-minimum-${type.id}`] = `gt:0`
    })
  }
  if (transportationTypeKeys.includes(1) && Array.isArray(dataEdit.value?.completionTimes?.contCategory)) {
    dataEdit.value.completionTimes.contCategory.forEach((type: any) => {
      rules[`contCategory-average-${type.id}`] = `gt:0`
      rules[`contCategory-minimum-${type.id}`] = `gt:0`
    })
  }
  return rules
})

defineExpose({
  open
})
</script>
